// App router — three buckets:
//   /login            standalone (Login renders its own shell)
//   /admin/*          AdminShell (Compliance/Manager layout)
//   everything else   AppShell (DevOps daily-driver layout)
//
// v241 — first-run setup wizard (item 76). When /api/public-config
// reports setup_required=true, the SetupRedirect wrapper bounces
// authenticated traffic to /setup before any feature page renders.
// /login + /setup + /admin/system/setup are exempt from the redirect
// so the wizard itself + the manual re-run path stay reachable.

import { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { usePublicConfig } from './api/publicConfig';
import { useWhoami } from './api/client';
import type { ReactNode } from 'react';
import { Home } from './pages/Home';
import { Repos } from './pages/Repos';
import { RepoDetail } from './pages/RepoDetail';
import { ArtifactDetail } from './pages/ArtifactDetail';
import { Projects } from './pages/Projects';
import { Tokens } from './pages/Tokens';
import { Login } from './pages/Login';
// Admin shell pages are lazy-loaded (item 250, batch 1): they're a
// governance surface visited far less often than the AppShell daily
// driver, so keeping them out of the main chunk shrinks the bundle
// everyone downloads on first paint. AdminSystemSetup stays eager —
// it's on the pre-auth first-run recovery path (see RequireAuth
// below) and isn't worth the Suspense flash on that rarely-hit route.
const AdminOverview = lazy(() =>
  import('./pages/admin/AdminOverview').then((m) => ({ default: m.AdminOverview })),
);
const AdminMaintenance = lazy(() =>
  import('./pages/admin/AdminMaintenance').then((m) => ({ default: m.AdminMaintenance })),
);
// v185 IA cleanup (item 18): the three notifications-adjacent pages
// and the two imports pages are consolidated into hubs. Old paths
// (/admin/notification-channels, /admin/webhooks, /admin/imports/bulk)
// redirect into the hub with the right tab pre-selected; the
// individual page exports remain for any direct importers.
const AdminNotificationsHub = lazy(() =>
  import('./pages/admin/AdminNotificationsHub').then((m) => ({ default: m.AdminNotificationsHub })),
);
const AdminImportsHub = lazy(() =>
  import('./pages/admin/AdminImportsHub').then((m) => ({ default: m.AdminImportsHub })),
);
const AdminAudit = lazy(() =>
  import('./pages/admin/AdminAudit').then((m) => ({ default: m.AdminAudit })),
);
const AdminPermissions = lazy(() =>
  import('./pages/admin/AdminPermissions').then((m) => ({ default: m.AdminPermissions })),
);
const AdminQuotas = lazy(() =>
  import('./pages/admin/AdminQuotas').then((m) => ({ default: m.AdminQuotas })),
);
const AdminSCIM = lazy(() =>
  import('./pages/admin/AdminSCIM').then((m) => ({ default: m.AdminSCIM })),
);
const AdminLicense = lazy(() =>
  import('./pages/admin/AdminLicense').then((m) => ({ default: m.AdminLicense })),
);
const AdminBranding = lazy(() =>
  import('./pages/admin/AdminBranding').then((m) => ({ default: m.AdminBranding })),
);
const AdminToolSwitcher = lazy(() =>
  import('./pages/admin/AdminToolSwitcher').then((m) => ({ default: m.AdminToolSwitcher })),
);
const AdminBanners = lazy(() =>
  import('./pages/admin/AdminBanners').then((m) => ({ default: m.AdminBanners })),
);
const AdminNetwork = lazy(() =>
  import('./pages/admin/AdminNetwork').then((m) => ({ default: m.AdminNetwork })),
);
const AdminTrust = lazy(() =>
  import('./pages/admin/AdminTrust').then((m) => ({ default: m.AdminTrust })),
);
const AdminRetention = lazy(() =>
  import('./pages/admin/AdminRetention').then((m) => ({ default: m.AdminRetention })),
);
const AdminCNPGCatalog = lazy(() =>
  import('./pages/admin/AdminCNPGCatalog').then((m) => ({ default: m.AdminCNPGCatalog })),
);
const AdminPullGate = lazy(() =>
  import('./pages/admin/AdminPullGate').then((m) => ({ default: m.AdminPullGate })),
);
const AdminGeo = lazy(() =>
  import('./pages/admin/AdminGeo').then((m) => ({ default: m.AdminGeo })),
);
const AdminApiReference = lazy(() =>
  import('./pages/admin/AdminApiReference').then((m) => ({ default: m.AdminApiReference })),
);
const AdminSeed = lazy(() =>
  import('./pages/admin/AdminSeed').then((m) => ({ default: m.AdminSeed })),
);
const AdminSlugAudit = lazy(() =>
  import('./pages/admin/AdminSlugAudit').then((m) => ({ default: m.AdminSlugAudit })),
);
const AdminCompliance = lazy(() =>
  import('./pages/admin/AdminCompliance').then((m) => ({ default: m.AdminCompliance })),
);
const AdminObservability = lazy(() =>
  import('./pages/admin/AdminObservability').then((m) => ({ default: m.AdminObservability })),
);
const AdminSupport = lazy(() =>
  import('./pages/admin/AdminSupport').then((m) => ({ default: m.AdminSupport })),
);
import { ServiceAccountsPage } from './pages/ServiceAccounts';
import { ProjectMembers } from './pages/ProjectMembers';
import { LicenseCompliance } from './pages/projects/LicenseCompliance';
import { OllamaModels } from './pages/projects/OllamaModels';
import { KustomizeComponents } from './pages/projects/KustomizeComponents';
import { OPABundles } from './pages/projects/OPABundles';
import { AgentSkillPackages } from './pages/projects/AgentSkillPackages';
import { McpServers } from './pages/projects/McpServers';
import { Quarantine } from './pages/projects/Quarantine';
import { Builds } from './pages/Builds';
import { ProjectBuilds } from './pages/projects/Builds';
import { BuildDetail } from './pages/projects/BuildDetail';
import { BuildDependencySearch } from './pages/projects/BuildDependencySearch';
import { Trash } from './pages/Trash';
import { Settings } from './pages/Settings';
import { Promotions } from './pages/Promotions';
import { Search } from './pages/Search';
// Security shell pages are lazy-loaded (item 250, batch 2): same
// rationale as the Admin shell in batch 1 — a governance surface
// visited far less often than the AppShell daily driver.
const SecurityList = lazy(() =>
  import('./pages/security/SecurityList').then((m) => ({ default: m.SecurityList })),
);
const SecurityNew = lazy(() =>
  import('./pages/security/SecurityNew').then((m) => ({ default: m.SecurityNew })),
);
const SecurityAudit = lazy(() =>
  import('./pages/security/SecurityAudit').then((m) => ({ default: m.SecurityAudit })),
);
const SecurityWaivers = lazy(() =>
  import('./pages/security/SecurityWaivers').then((m) => ({ default: m.SecurityWaivers })),
);
const WhoPulled = lazy(() =>
  import('./pages/security/WhoPulled').then((m) => ({ default: m.WhoPulled })),
);
const Advisories = lazy(() =>
  import('./pages/security/Advisories').then((m) => ({ default: m.Advisories })),
);
const AdvisoryDetail = lazy(() =>
  import('./pages/security/AdvisoryDetail').then((m) => ({ default: m.AdvisoryDetail })),
);
// Detection shell pages are lazy-loaded (item 250, batch 3): same
// rationale as the Admin/Security shells in batches 1-2.
// Project scans (2026-10-06): lazy like the Detection pages — not every
// session opens it, so it stays out of the entry chunk (item 250 budget).
const ProjectScansPage = lazy(() =>
  import('./pages/ProjectScans').then((m) => ({ default: m.ProjectScansPage })),
);
const DetectionFindings = lazy(() =>
  import('./pages/detection/Findings').then((m) => ({ default: m.DetectionFindings })),
);
const DetectionFindingDetail = lazy(() =>
  import('./pages/detection/FindingDetail').then((m) => ({ default: m.DetectionFindingDetail })),
);
const DetectionRules = lazy(() =>
  import('./pages/detection/Rules').then((m) => ({ default: m.DetectionRules })),
);
const DetectionScanners = lazy(() =>
  import('./pages/detection/Scanners').then((m) => ({ default: m.DetectionScanners })),
);
const DetectionQueue = lazy(() =>
  import('./pages/detection/Queue').then((m) => ({ default: m.DetectionQueue })),
);
const DetectionDashboard = lazy(() =>
  import('./pages/detection/Dashboard').then((m) => ({ default: m.DetectionDashboard })),
);
import { AppShell } from './components/AppShell';
import { SetupWizard } from './pages/SetupWizard';
import { AdminSystemSetup } from './pages/admin/AdminSystemSetup';

// SetupRedirect bounces authenticated traffic to /setup whenever the
// public-config endpoint says the wizard hasn't been completed. Paths
// that the wizard itself uses (/setup) or that an admin needs to
// recover (/login, /admin/system/setup) are passed through unchanged.
function SetupRedirect({ children }: Readonly<{ children: ReactNode }>) {
  const { data, isLoading } = usePublicConfig();
  const { pathname } = useLocation();
  const exempt =
    pathname === '/login' ||
    pathname === '/setup' ||
    pathname.startsWith('/admin/system/setup');
  if (isLoading || !data) return <>{children}</>;
  if (data.setup_required && !exempt) return <Navigate to="/setup" replace />;
  return <>{children}</>;
}

// RequireAuth gates the whole app behind a resolved session so an
// unauthenticated visitor never sees the app chrome (sidebar, pages) —
// they land on /login instead. /login, /setup and the break-glass setup
// path stay open so sign-in and first-run recovery remain reachable.
function RequireAuth({ children }: Readonly<{ children: ReactNode }>) {
  const { user, loading, error } = useWhoami();
  const { pathname } = useLocation();
  const open =
    pathname === '/login' ||
    pathname === '/setup' ||
    pathname.startsWith('/admin/system/setup');
  if (open) return <>{children}</>;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="starfield-bg" aria-hidden="true" />
        <Loader2 className="h-6 w-6 animate-spin text-accent" aria-label="Loading" />
      </div>
    );
  }
  if (error || !user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Suspense fallback for lazy-loaded route chunks (item 250, batch 1).
// Deliberately minimal — it's shown for a single network round-trip
// while a route's own chunk downloads, not a full-page loading state.
function RouteFallback() {
  return (
    <div className="flex items-center justify-center p-12">
      <Loader2 className="h-6 w-6 animate-spin text-accent" aria-label="Loading" />
    </div>
  );
}

export function App() {
  return (
    <SetupRedirect>
    <RequireAuth>
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* v241 — first-run setup wizard (item 76 of PRODUCT-ROADMAP.md). */}
      <Route path="/setup" element={<SetupWizard />} />
      <Route path="/admin/system/setup" element={<AdminSystemSetup />} />

      <Route path="/admin" element={<Suspense fallback={<RouteFallback />}><AdminOverview /></Suspense>} />
      <Route path="/admin/maintenance" element={<Suspense fallback={<RouteFallback />}><AdminMaintenance /></Suspense>} />

      {/* v185 IA cleanup — Notifications hub. */}
      <Route path="/admin/notifications" element={<Suspense fallback={<RouteFallback />}><AdminNotificationsHub /></Suspense>} />
      <Route
        path="/admin/notification-channels"
        element={<Navigate replace to="/admin/notifications?tab=channels" />}
      />
      <Route
        path="/admin/webhooks"
        element={<Navigate replace to="/admin/notifications?tab=webhooks" />}
      />

      <Route path="/admin/audit" element={<Suspense fallback={<RouteFallback />}><AdminAudit /></Suspense>} />
      <Route path="/admin/permissions" element={<Suspense fallback={<RouteFallback />}><AdminPermissions /></Suspense>} />
      <Route path="/admin/quotas" element={<Suspense fallback={<RouteFallback />}><AdminQuotas /></Suspense>} />
      <Route path="/admin/scim" element={<Suspense fallback={<RouteFallback />}><AdminSCIM /></Suspense>} />

      {/* v185 IA cleanup — Imports hub. */}
      <Route path="/admin/imports" element={<Suspense fallback={<RouteFallback />}><AdminImportsHub /></Suspense>} />
      <Route
        path="/admin/imports/bulk"
        element={<Navigate replace to="/admin/imports?tab=bulk" />}
      />
      <Route path="/admin/license" element={<Suspense fallback={<RouteFallback />}><AdminLicense /></Suspense>} />
      <Route path="/admin/branding" element={<Suspense fallback={<RouteFallback />}><AdminBranding /></Suspense>} />
      <Route path="/admin/tool-switcher" element={<Suspense fallback={<RouteFallback />}><AdminToolSwitcher /></Suspense>} />
      <Route path="/admin/banners" element={<Suspense fallback={<RouteFallback />}><AdminBanners /></Suspense>} />
      {/* v220 air-gapped-mode-by-default settings (item 24 of PRODUCT-ROADMAP.md). */}
      <Route path="/admin/network" element={<Suspense fallback={<RouteFallback />}><AdminNetwork /></Suspense>} />
      {/* Consolidated trust surface — X.509 / Sigstore / OIDC tabs
          under one shell (item 91 Phase A of PRODUCT-ROADMAP.md). The
          three legacy URLs redirect onto the matching tab fragment so
          existing bookmarks keep working. */}
      <Route path="/admin/trust" element={<Suspense fallback={<RouteFallback />}><AdminTrust /></Suspense>} />
      <Route
        path="/admin/sigstore"
        element={<Navigate replace to="/admin/trust#sigstore" />}
      />
      <Route
        path="/admin/oidc/policies"
        element={<Navigate replace to="/admin/trust#oidc" />}
      />
      {/* Org-wide retention overview (item 40 of PRODUCT-ROADMAP.md). */}
      <Route path="/admin/retention" element={<Suspense fallback={<RouteFallback />}><AdminRetention /></Suspense>} />
      {/* CNPG ImageCatalog mirror (item 46 of PRODUCT-ROADMAP.md). */}
      <Route path="/admin/cnpg-catalog" element={<Suspense fallback={<RouteFallback />}><AdminCNPGCatalog /></Suspense>} />
      {/* Pull-Gate-Policy-Engine (item 47 of PRODUCT-ROADMAP.md, Phase B). */}
      <Route path="/admin/pull-gate" element={<Suspense fallback={<RouteFallback />}><AdminPullGate /></Suspense>} />
      {/* Cross-Instance Geo-Sync (item 48 of PRODUCT-ROADMAP.md, Phase A). */}
      <Route path="/admin/geo" element={<Suspense fallback={<RouteFallback />}><AdminGeo /></Suspense>} />
      {/* In-app API playground (item 55 Phase D / item 64 in delivery order). */}
      <Route path="/admin/api-reference" element={<Suspense fallback={<RouteFallback />}><AdminApiReference /></Suspense>} />
      <Route path="/admin/seed" element={<Suspense fallback={<RouteFallback />}><AdminSeed /></Suspense>} />
      {/* Slug-naming-convention audit (item 62 of PRODUCT-ROADMAP.md). */}
      <Route path="/admin/slug-audit" element={<Suspense fallback={<RouteFallback />}><AdminSlugAudit /></Suspense>} />
      {/* Consolidated compliance surface — ISO 27001 / SOC 2 tabs
          under one shell (item 91 Phase B of PRODUCT-ROADMAP.md). The
          two legacy URLs redirect onto the matching tab fragment so
          existing bookmarks keep working. */}
      <Route path="/admin/compliance" element={<Suspense fallback={<RouteFallback />}><AdminCompliance /></Suspense>} />
      <Route
        path="/admin/compliance/iso27001"
        element={<Navigate replace to="/admin/compliance#iso27001" />}
      />
      <Route
        path="/admin/compliance/soc2"
        element={<Navigate replace to="/admin/compliance#soc2" />}
      />
      {/* Platform-Monitoring observability snapshot (item 70 Phase F). */}
      <Route path="/admin/observability" element={<Suspense fallback={<RouteFallback />}><AdminObservability /></Suspense>} />
      {/* Admin → Support — UI variant of `orbital diag` bundle generator
          (item 100 Phase B of PRODUCT-ROADMAP.md). Backend endpoint
          /api/admin/support/generate-bundle landed in Phase A. */}
      <Route path="/admin/support" element={<Suspense fallback={<RouteFallback />}><AdminSupport /></Suspense>} />

      <Route path="/security" element={<Suspense fallback={<RouteFallback />}><SecurityList /></Suspense>} />
      <Route path="/security/new" element={<Suspense fallback={<RouteFallback />}><SecurityNew /></Suspense>} />
      <Route path="/security/waivers" element={<Suspense fallback={<RouteFallback />}><SecurityWaivers /></Suspense>} />
      <Route path="/security/audit" element={<Suspense fallback={<RouteFallback />}><SecurityAudit /></Suspense>} />
      <Route path="/security/who-pulled" element={<Suspense fallback={<RouteFallback />}><WhoPulled /></Suspense>} />
      <Route path="/security/advisories" element={<Suspense fallback={<RouteFallback />}><Advisories /></Suspense>} />
      <Route path="/security/advisories/:id" element={<Suspense fallback={<RouteFallback />}><AdvisoryDetail /></Suspense>} />

      {/* v161 Tier 0 Detection space — sibling to Security in the IA. */}
      <Route path="/detection/findings" element={<Suspense fallback={<RouteFallback />}><DetectionFindings /></Suspense>} />
      <Route path="/detection/findings/:id" element={<Suspense fallback={<RouteFallback />}><DetectionFindingDetail /></Suspense>} />
      <Route path="/detection/rules" element={<Suspense fallback={<RouteFallback />}><DetectionRules /></Suspense>} />
      <Route path="/detection/scanners" element={<Suspense fallback={<RouteFallback />}><DetectionScanners /></Suspense>} />
      <Route path="/detection/queue" element={<Suspense fallback={<RouteFallback />}><DetectionQueue /></Suspense>} />
      {/* v177 Tier 4 — aggregations dashboard. */}
      <Route path="/detection/dashboard" element={<Suspense fallback={<RouteFallback />}><DetectionDashboard /></Suspense>} />

      <Route
        path="/*"
        element={
          <AppShell>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/repos" element={<Repos />} />
              <Route path="/repos/:id" element={<RepoDetail />} />
              {/* Ollama-specific model browser (roadmap item 38). */}
              <Route path="/repos/:id/ollama" element={<OllamaModels />} />
              {/* Kustomize-Components OCI browser (roadmap item 43). */}
              <Route path="/repos/:id/kustomize" element={<KustomizeComponents />} />
              {/* OPA-Bundles browser (roadmap item 45). */}
              <Route path="/repos/:id/opa" element={<OPABundles />} />
              {/* Agent/Skill package browser (roadmap item 149 Phase B). */}
              <Route path="/repos/:id/agentskill" element={<AgentSkillPackages />} />
              <Route path="/repos/:id/mcp" element={<McpServers />} />
              {/* v193 — per-artifact detail page (item 17). Currently
                  surfaces the dependency-graph tab; future tabs land
                  on the same path. */}
              <Route path="/artifacts/:id" element={<ArtifactDetail />} />
              <Route path="/tokens" element={<Tokens />} />
              <Route path="/promotions" element={<Promotions />} />
              <Route path="/scans" element={<Suspense fallback={<RouteFallback />}><ProjectScansPage /></Suspense>} />
              {/* v222 — Build-Info browse (item 33 Phase A). */}
              <Route path="/builds" element={<Builds />} />
              <Route path="/projects/:id/builds" element={<ProjectBuilds />} />
              {/* v224 — reverse dependency search (item 33 Phase C).
                  Mounted before the {name}/{number} route so React-Router
                  doesn't try to interpret "dependency-search" as a build
                  name. */}
              <Route
                path="/projects/:id/builds/dependency-search"
                element={<BuildDependencySearch />}
              />
              <Route
                path="/projects/:id/builds/:name/:number"
                element={<BuildDetail />}
              />
              {/* v186 — global artifact + repo search results page. */}
              <Route path="/search" element={<Search />} />
              <Route path="/service-accounts" element={<ServiceAccountsPage />} />
              <Route path="/projects/:id/members" element={<ProjectMembers />} />
              {/* v191 — per-project license-compliance dashboard. */}
              <Route
                path="/projects/:id/license-compliance"
                element={<LicenseCompliance />}
              />
              {/* v192 — per-project auto-quarantine settings (item 16). */}
              <Route
                path="/projects/:id/quarantine"
                element={<Quarantine />}
              />
              <Route path="/trash" element={<Trash />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </AppShell>
        }
      />
    </Routes>
    </RequireAuth>
    </SetupRedirect>
  );
}
